import { useState } from "react";
import { motion } from "framer-motion";
import { Plus, Pause, Ban, CheckCircle2, Download, FileText, AlertCircle, AlertTriangle, ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { TRAMITES } from "../data/content";

export const Tramites = () => {
  const [bajas, setBajas] = useState(null);
  const [equiv, setEquiv] = useState(null);
  const [constancia, setConstancia] = useState(null);
  const [info, setInfo] = useState(null);

  return (
    <section id="tramites" data-testid="tramites-section" className="py-24 md:py-32 bg-[#F4F2F7]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="max-w-2xl mb-14">
          <p className="text-xs uppercase tracking-overline text-[#5B3E96] font-semibold mb-5">
            {TRAMITES.overline}
          </p>
          <h2 className="font-serif-display text-3xl md:text-5xl font-semibold text-[#2D1B4E] tracking-tight leading-[1.1]">
            {TRAMITES.title}
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {TRAMITES.items.map((t, i) => {
            const Icon = t.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                data-testid={`tramite-card-${i}`}
                className="bg-white border border-[#E8E4EE] rounded-sm p-7 flex flex-col hover:-translate-y-1 transition-transform duration-300"
              >
                <Icon strokeWidth={1.5} className="w-8 h-8 text-[#5B3E96]" />
                <h3 className="font-serif-display text-xl font-semibold text-[#2D1B4E] mt-4">
                  {t.title}
                </h3>
                <p className="text-sm text-[#5F5670] mt-2 leading-relaxed">{t.desc}</p>
                {t.bajas && (
                  <button
                    type="button"
                    onClick={() => setBajas(t.bajas)}
                    data-testid={`tramite-vermas-${i}`}
                    className="mt-5 inline-flex items-center gap-2 text-sm font-medium self-start rounded-sm px-4 py-2 bg-[#2D1B4E] text-white hover:bg-[#1A1523] transition-colors"
                  >
                    <Plus className="w-4 h-4" /> Ver más
                  </button>
                )}
                {t.equivalencia && (
                  <button
                    type="button"
                    onClick={() => setEquiv(t.equivalencia)}
                    data-testid={`tramite-vermas-${i}`}
                    className="mt-5 inline-flex items-center gap-2 text-sm font-medium self-start rounded-sm px-4 py-2 bg-[#2D1B4E] text-white hover:bg-[#1A1523] transition-colors"
                  >
                    <Plus className="w-4 h-4" /> Ver más
                  </button>
                )}
                {t.constancias && (
                  <button
                    type="button"
                    onClick={() => setConstancia(t.constancias)}
                    data-testid={`tramite-vermas-${i}`}
                    className="mt-5 inline-flex items-center gap-2 text-sm font-medium self-start rounded-sm px-4 py-2 bg-[#2D1B4E] text-white hover:bg-[#1A1523] transition-colors"
                  >
                    <Plus className="w-4 h-4" /> Ver más
                  </button>
                )}
                {t.info && (
                  <button
                    type="button"
                    onClick={() => setInfo(t.info)}
                    data-testid={`tramite-vermas-${i}`}
                    className="mt-5 inline-flex items-center gap-2 text-sm font-medium self-start rounded-sm px-4 py-2 bg-[#2D1B4E] text-white hover:bg-[#1A1523] transition-colors"
                  >
                    <Plus className="w-4 h-4" /> Ver más
                  </button>
                )}
              </motion.div>
            );
          })}
        </div>

        <p className="text-sm text-[#5F5670] mt-8 border-l-2 border-[#5B3E96] pl-4">
          {TRAMITES.note}
        </p>
      </div>

      <Dialog open={!!bajas} onOpenChange={(o) => !o && setBajas(null)}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="bajas-modal">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl text-[#2D1B4E] text-left">
              Bajas escolares
            </DialogTitle>
          </DialogHeader>

          {bajas && (
            <div className="mt-1">
              <p className="text-sm text-[#5F5670] leading-relaxed">{bajas.intro}</p>

              <div className="grid sm:grid-cols-2 gap-4 mt-6">
                <div className="bg-[#F4F2F7] border border-[#E8E4EE] rounded-sm p-5">
                  <div className="flex items-center gap-2 text-[#5B3E96]">
                    <Pause className="w-5 h-5" />
                    <h4 className="font-serif-display text-lg font-semibold text-[#2D1B4E]">
                      {bajas.temporal.title}
                    </h4>
                  </div>
                  <ul className="mt-3 space-y-2">
                    {bajas.temporal.points.map((p, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-[#1A1523] leading-relaxed">
                        <span className="text-[#5B3E96] mt-1">•</span> {p}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-[#2D1B4E] text-white rounded-sm p-5">
                  <div className="flex items-center gap-2 text-[#C7B8E6]">
                    <Ban className="w-5 h-5" />
                    <h4 className="font-serif-display text-lg font-semibold text-white">
                      {bajas.definitiva.title}
                    </h4>
                  </div>
                  <ul className="mt-3 space-y-2">
                    {bajas.definitiva.points.map((p, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-white/85 leading-relaxed">
                        <span className="text-[#C7B8E6] mt-1">•</span> {p}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-7">
                <p className="text-xs uppercase tracking-overline text-[#5B3E96] font-semibold mb-4">
                  ¿Cómo solicitar una baja?
                </p>
                <ol className="space-y-4">
                  {bajas.steps.map((s, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-[#2D1B4E] text-white text-xs font-semibold shrink-0">
                        {i + 1}
                      </span>
                      <div className="mt-0.5">
                        <p className="text-sm font-semibold text-[#2D1B4E]">{s.title}</p>
                        {s.desc && (
                          <p className="text-sm text-[#5F5670] leading-relaxed mt-0.5">{s.desc}</p>
                        )}
                        {s.sub && (
                          <ul className="mt-1.5 space-y-1.5">
                            {s.sub.map((p, j) => (
                              <li key={j} className="flex items-start gap-2 text-sm text-[#1A1523] leading-relaxed">
                                <span className="text-[#5B3E96] mt-1">•</span> {p}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
              </div>

              {bajas.important && (
                <div className="mt-7 bg-[#FBECEC] border border-[#E7C9C9] rounded-sm p-5">
                  <p className="text-sm font-semibold text-[#A23B3B] mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" /> Importante
                  </p>
                  <ul className="space-y-2">
                    {bajas.important.map((m, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-[#7A3B3B] leading-relaxed">
                        <span className="mt-1">•</span> {m}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-7 bg-[#F4F2F7] border border-[#E8E4EE] rounded-sm p-5">
                <p className="text-sm font-medium text-[#2D1B4E] mb-3">
                  Motivos de baja que puedes seleccionar
                </p>
                <p className="text-xs uppercase tracking-overline text-[#5B3E96] font-semibold mb-3">
                  {bajas.motivosTitle}
                </p>
                <div className="flex flex-wrap gap-2">
                  {bajas.motivos.map((m, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1.5 bg-white border border-[#E8E4EE] rounded-full px-3 py-1.5 text-xs text-[#1A1523]"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5 text-[#5B3E96]" />
                      {m.replace(/\.$/, "")}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-7 pt-6 border-t border-[#E8E4EE]">
                <p className="text-xs uppercase tracking-overline text-[#5B3E96] font-semibold mb-4">
                  Formatos para descargar
                </p>
                <div className="space-y-3">
                  {bajas.documents.map((d, i) => (
                    <a
                      key={i}
                      href={d.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`baja-doc-${i}`}
                      className="flex items-center justify-between gap-3 bg-[#F4F2F7] hover:bg-[#EDE8F5] border border-[#E8E4EE] rounded-sm px-4 py-3 transition-colors group"
                    >
                      <span className="flex items-center gap-3 text-sm text-[#1A1523]">
                        <FileText className="w-4 h-4 text-[#5B3E96] shrink-0" />
                        {d.label}
                      </span>
                      <Download className="w-4 h-4 text-[#5B3E96] group-hover:translate-y-0.5 transition-transform shrink-0" />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!equiv} onOpenChange={(o) => !o && setEquiv(null)}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="equivalencia-modal">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl text-[#2D1B4E] text-left">
              Equivalencias y revalidación
            </DialogTitle>
          </DialogHeader>

          {equiv && (
            <div className="mt-1">
              <p className="text-sm text-[#5F5670] leading-relaxed">{equiv.intro}</p>

              <p className="text-xs uppercase tracking-overline text-[#5B3E96] font-semibold mt-6 mb-4">
                ¿Cómo realizar el trámite?
              </p>
              <div className="space-y-5">
                {equiv.steps.map((s, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="flex items-center justify-center w-7 h-7 rounded-full bg-[#2D1B4E] text-white text-xs font-semibold shrink-0">
                      {i + 1}
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-[#2D1B4E]">{s.title}</p>
                      <ul className="mt-1.5 space-y-1.5">
                        {s.sub.map((p, j) => {
                          const m = p.match(/([\w.+-]+@[\w.-]+\.\w+)/);
                          return (
                            <li key={j} className="flex items-start gap-2 text-sm text-[#5F5670] leading-relaxed">
                              <span className="text-[#5B3E96] mt-1">•</span>
                              <span>
                                {m ? (
                                  <>
                                    {p.slice(0, m.index)}
                                    <a
                                      href={`mailto:${m[1]}`}
                                      data-testid="equivalencia-email"
                                      className="font-bold text-[#5B3E96] underline decoration-2 underline-offset-2 hover:text-[#2D1B4E]"
                                    >
                                      {m[1]}
                                    </a>
                                    {p.slice(m.index + m[1].length)}
                                  </>
                                ) : (
                                  p
                                )}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-7 bg-[#FBF7E9] border border-[#EADFAE] rounded-sm p-5">
                <p className="text-sm font-semibold text-[#7A5B12] flex items-center gap-2 mb-3">
                  <AlertCircle className="w-4 h-4" /> Aspectos importantes
                </p>
                <ul className="space-y-2">
                  {equiv.important.map((m, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#5C4A15] leading-relaxed">
                      <span className="mt-1">•</span> {m}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-6">
                <p className="text-xs uppercase tracking-overline text-[#5B3E96] font-semibold mb-3">
                  Requisitos para que proceda la equivalencia
                </p>
                <ul className="space-y-3">
                  {equiv.requisitos.map((r, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle2 strokeWidth={1.5} className="w-5 h-5 text-[#5B3E96] shrink-0 mt-0.5" />
                      <span className="text-sm text-[#1A1523] leading-relaxed">{r}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <a
                href={equiv.link.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="equivalencia-link"
                className="mt-7 flex items-center justify-between gap-3 bg-[#2D1B4E] text-white rounded-sm px-5 py-4 hover:bg-[#1A1523] transition-colors group"
              >
                <span className="text-sm leading-snug">{equiv.link.note}</span>
                <ExternalLink className="w-5 h-5 shrink-0 group-hover:translate-x-0.5 transition-transform" />
              </a>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!constancia} onOpenChange={(o) => !o && setConstancia(null)}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="constancias-modal">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl text-[#2D1B4E] text-left">
              Constancias
            </DialogTitle>
          </DialogHeader>

          {constancia && (
            <div className="mt-1">
              <p className="text-xs uppercase tracking-overline text-[#5B3E96] font-semibold mb-4">
                ¿Qué tipos de constancias puedes solicitar?
              </p>
              <div className="space-y-3">
                {constancia.tipos.map((c, i) => (
                  <div key={i} className="bg-[#F4F2F7] border border-[#E8E4EE] rounded-sm p-4">
                    <p className="text-sm font-semibold text-[#2D1B4E]">{c.t}</p>
                    <p className="text-sm text-[#5F5670] mt-1 leading-relaxed">{c.d}</p>
                  </div>
                ))}
              </div>

              <p className="text-xs uppercase tracking-overline text-[#5B3E96] font-semibold mt-7 mb-4">
                ¿Cómo solicitar una constancia?
              </p>
              <ol className="space-y-3">
                {constancia.steps.map((s, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-[#2D1B4E] text-white text-xs font-semibold shrink-0">
                      {i + 1}
                    </span>
                    <span className="text-sm text-[#1A1523] leading-relaxed mt-0.5">{s}</span>
                  </li>
                ))}
              </ol>

              <div className="grid sm:grid-cols-2 gap-4 mt-7">
                <div className="bg-[#F4F2F7] border border-[#E8E4EE] rounded-sm px-4 py-3">
                  <p className="text-[10px] uppercase tracking-overline text-[#5F5670]">Tiempo de entrega</p>
                  <p className="text-sm text-[#1A1523] mt-0.5 leading-relaxed">{constancia.delivery}</p>
                </div>
                <div className="bg-[#2D1B4E] text-white rounded-sm px-4 py-3">
                  <p className="text-[10px] uppercase tracking-overline text-white/60">Costo</p>
                  <p className="text-sm mt-0.5 leading-relaxed">{constancia.cost}</p>
                </div>
              </div>

              <p className="text-xs uppercase tracking-overline text-[#5B3E96] font-semibold mt-7 mb-3">
                Requisitos
              </p>
              <ul className="space-y-3">
                {constancia.requisitos.map((r, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 strokeWidth={1.5} className="w-5 h-5 text-[#5B3E96] shrink-0 mt-0.5" />
                    <span className="text-sm text-[#1A1523] leading-relaxed">{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!info} onOpenChange={(o) => !o && setInfo(null)}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="info-modal">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl text-[#2D1B4E] text-left">
              {info?.title}
            </DialogTitle>
          </DialogHeader>

          {info && (
            <div className="mt-1">
              {info.blocks.map((b, bi) => (
                <div key={bi} className={bi === 0 ? "" : "mt-6"}>
                  {b.type === "tagline" && (
                    <p className="font-serif-display text-xl text-[#5B3E96]">{b.text}</p>
                  )}
                  {b.type === "text" && (
                    <p className="text-sm text-[#5F5670] leading-relaxed">{b.text}</p>
                  )}
                  {b.type === "note" && (
                    <div className="bg-[#FBF7E9] border border-[#EADFAE] rounded-sm p-4 flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-[#7A5B12] shrink-0 mt-0.5" />
                      <p className="text-sm text-[#5C4A15] leading-relaxed"><span className="font-semibold">Nota:</span> {b.text}</p>
                    </div>
                  )}
                  {b.type === "steps" && (
                    <>
                      {b.title && (
                        <p className="text-xs uppercase tracking-overline text-[#5B3E96] font-semibold mb-4">{b.title}</p>
                      )}
                      <ol className="space-y-3">
                        {b.items.map((s, i) => (
                          <li key={i} className="flex items-start gap-3">
                            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-[#2D1B4E] text-white text-xs font-semibold shrink-0">
                              {i + 1}
                            </span>
                            <span className="text-sm text-[#1A1523] leading-relaxed mt-0.5">{s}</span>
                          </li>
                        ))}
                      </ol>
                    </>
                  )}
                  {b.type === "list" && (
                    <>
                      {b.title && (
                        <p className="text-xs uppercase tracking-overline text-[#5B3E96] font-semibold mb-3">{b.title}</p>
                      )}
                      <ul className="space-y-2.5">
                        {b.items.map((it, i) => (
                          <li key={i} className="flex items-start gap-3 text-sm text-[#1A1523] leading-relaxed">
                            {b.variant === "check" ? (
                              <CheckCircle2 strokeWidth={1.5} className="w-5 h-5 text-[#5B3E96] shrink-0 mt-0.5" />
                            ) : (
                              <span className="text-[#5B3E96] mt-1">•</span>
                            )}
                            <span>{it}</span>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
